// Sections 1-3: Items 1-12
export const lessonsS1S3 = [
  {
    id: 1, sectionId: 1, type: 'lesson', xp: 50,
    title: 'The Internet is Made by People',
    subtitle: 'And you can be one of them',
    content: [
      { type: 'hook', value: 'Right now, you are looking at a website. Someone — a real person, not a robot — wrote every single line of code that makes it work. By the end of this course, you will know how to do the same thing.' },
      { type: 'text', value: 'Every website you visit — Google, YouTube, Instagram — was built by people who once knew nothing about coding. They all started exactly where you are right now.' },
      { type: 'text', value: 'The internet is not made by computers. It is made by people who learned how to tell computers what to show on screen. That is all a website is — instructions written in a language that browsers understand.' },
      { type: 'heading', value: 'The "View Page Source" Secret' },
      { type: 'text', value: 'Here is something amazing: you can see the code behind ANY website. Right now. Let us try it!' },
      { type: 'code', lang: 'html', title: 'What website code looks like', code: '<!DOCTYPE html>\n<html>\n<head>\n  <title>Google</title>\n</head>\n<body>\n  <h1>Welcome to Google</h1>\n  <input type="text" placeholder="Search...">\n</body>\n</html>' },
      { type: 'tip', value: 'This might look confusing right now. That is totally okay! By the end of this course, you will read code like this easily.' },
    ],
    task: {
      title: 'Your First Mission',
      steps: [
        'Open Google Chrome on your computer',
        'Go to any website you like (try google.com)',
        'Right-click anywhere on the page',
        'Click "View Page Source"',
        'Look at all that code! Someone wrote every line of it!',
      ],
    },
    challenge: {
      title: 'Explorer Challenge',
      description: 'Visit 3 different websites and view their source code. Try to find an <h1> tag in each one. Write down what text is inside the <h1> tag for each website.',
    },
  },
  {
    id: 2, sectionId: 1, type: 'lesson', xp: 50,
    title: 'What HTML Actually Is',
    subtitle: 'The skeleton of every website',
    content: [
      { type: 'hook', value: 'HTML is the language that every single website in the world is built with. Without HTML, there would be no internet. And you are about to learn it!' },
      { type: 'text', value: 'Think of a website like a person. HTML is the skeleton — it gives the website its structure. CSS is the clothes and makeup — it makes things look pretty. JavaScript is the muscles — it makes things move.' },
      { type: 'text', value: 'Today we focus on the skeleton: HTML. HTML stands for HyperText Markup Language. Do not worry about the fancy name. It just means: a language for creating web pages.' },
      { type: 'heading', value: 'Before CSS vs After CSS' },
      { type: 'text', value: 'Look at the same page with and without CSS. HTML gives structure, CSS gives beauty:' },
      { type: 'comparison', before: { label: 'HTML Only (No CSS)', html: '<h1>My Website</h1><p>Welcome to my page! I love coding.</p><a href="#">Click here</a>' }, after: { label: 'HTML + CSS', html: '<h1>My Website</h1><p>Welcome to my page! I love coding.</p><a href="#">Click here</a>', css: 'h1{color:#FF0099;font-family:Arial;font-size:28px} p{color:#555;font-size:16px;line-height:1.6} a{color:#1E90FF;text-decoration:none;font-weight:bold}' } },
      { type: 'tip', value: 'The HTML is exactly the same in both! Only CSS changes how it looks. That is the magic of separating structure from style.' },
    ],
    task: {
      title: 'Observe the Difference',
      steps: [
        'Look at both previews above carefully',
        'Notice: the text content is the same',
        'The only difference is the styling (colors, fonts, sizes)',
        'HTML = what the content IS. CSS = how it LOOKS.',
      ],
    },
    challenge: {
      title: 'Think About It',
      description: 'Look at your favourite website. Can you imagine what it would look like without any CSS? Just plain text and links? Draw a rough picture of what you think it would look like!',
    },
  },
  {
    id: 3, sectionId: 1, type: 'lesson', xp: 50,
    title: 'What You Will Build',
    subtitle: 'A preview of your journey',
    content: [
      { type: 'hook', value: 'By the end of this course, you will build a beautiful personal profile page — all by yourself. Every single line of code will be written by YOU.' },
      { type: 'text', value: 'Here is a preview of what your final project will look like:' },
      { type: 'preview', title: 'Your Final Project Preview', html: '<div style="font-family:Arial;max-width:500px"><div style="background:linear-gradient(135deg,#FF0099,#1E90FF);padding:30px;border-radius:12px;text-align:center;color:white"><h1 style="margin:0;font-size:24px">Your Name Here</h1><p style="opacity:0.9;margin:5px 0">Student | Coder | Creator</p></div><div style="padding:20px"><h2 style="color:#FF0099;font-size:18px">About Me</h2><p style="color:#555;line-height:1.6">I am learning to code and building amazing things!</p><h2 style="color:#FF0099;font-size:18px">My Skills</h2><div style="display:flex;gap:8px;flex-wrap:wrap"><span style="background:#f0f0f0;padding:4px 12px;border-radius:20px;font-size:13px">HTML</span><span style="background:#f0f0f0;padding:4px 12px;border-radius:20px;font-size:13px">CSS</span><span style="background:#f0f0f0;padding:4px 12px;border-radius:20px;font-size:13px">Web Design</span></div></div></div>' },
      { type: 'text', value: 'Every single part of this — the colors, the layout, the text, the rounded corners — you will learn how to create. Step by step.' },
      { type: 'text', value: 'You will start with simple text on a white page. Then add headings, images, links. Then add beautiful colors, fonts, and layouts with CSS. By the end, you will have a page that looks professional.' },
      { type: 'warning', value: 'Every expert was once a beginner. The people who built Google, YouTube, and Instagram all started exactly like this.' },
    ],
    task: {
      title: 'Get Ready',
      steps: [
        'Make sure you have Google Chrome installed',
        'Make sure you have VS Code installed',
        'Take a deep breath — you are about to become a coder!',
      ],
    },
    challenge: {
      title: 'Dream Big',
      description: 'Write down on paper: What kind of website do you want to build? About yourself? About your hobbies? About your school? There is no wrong answer!',
    },
  },
  {
    id: 4, sectionId: 2, type: 'lesson', xp: 50,
    title: 'VS Code Tour',
    subtitle: 'Your coding home',
    content: [
      { type: 'hook', value: 'VS Code is where professional coders write their code every day. It is like a super-powered text editor made specially for coding. And it is free!' },
      { type: 'text', value: 'VS Code has a few important parts. Let us learn each one:' },
      { type: 'list', ordered: false, items: [
        'Explorer Panel (left side): Shows all your files and folders, like the file manager on your computer',
        'Editor Area (center): This is where you write your code. It has line numbers and color coding!',
        'Terminal (bottom): A text-based way to talk to your computer. We will use this later.',
        'Status Bar (very bottom): Shows useful info like which language you are writing in',
      ] },
      { type: 'text', value: 'The best part about VS Code? It color-codes your code! HTML tags show in one color, text in another, and attributes in a third. This makes it so much easier to read.' },
      { type: 'tip', value: 'You can change the theme (colors) of VS Code. Try going to File > Preferences > Color Theme and pick one you like!' },
    ],
    task: {
      title: 'Set Up Your Workspace',
      steps: [
        'Open VS Code on your computer',
        'Go to your Desktop',
        'Create a new folder called "my-website"',
        'In VS Code, click File > Open Folder',
        'Select the "my-website" folder you just created',
        'You should see the folder name in the Explorer panel on the left',
      ],
    },
    challenge: {
      title: 'Explore VS Code',
      description: 'Try changing the VS Code theme! Go to File > Preferences > Color Theme. Try at least 3 different themes and pick the one you like the most.',
    },
  },
  {
    id: 5, sectionId: 2, type: 'lesson', xp: 50,
    title: 'Your First HTML File',
    subtitle: 'The moment you become a web developer',
    content: [
      { type: 'hook', value: 'In the next 5 minutes, you will create a real website. Not a practice one. A REAL one that opens in your browser. Ready?' },
      { type: 'text', value: 'Every HTML file starts with a structure called the "boilerplate." Think of it like the foundation of a house — every house needs one, and it always looks the same.' },
      { type: 'code', lang: 'html', title: 'HTML Boilerplate — Type this exactly', code: '<!DOCTYPE html>\n<html>\n<head>\n  <title>My First Website</title>\n</head>\n<body>\n  <h1>Hello! I made this!</h1>\n  <p>This is my very first website.</p>\n</body>\n</html>' },
      { type: 'text', value: 'Let us understand each part:' },
      { type: 'list', ordered: false, items: [
        '<!DOCTYPE html> — Tells the browser "this is an HTML file"',
        '<html> — The container for everything',
        '<head> — Invisible info about the page (like the title in the browser tab)',
        '<title> — The text that appears in the browser tab',
        '<body> — Everything visible on the page goes here',
        '<h1> — A big heading',
        '<p> — A paragraph of text',
      ] },
      { type: 'preview', title: 'What Your Page Will Look Like', html: '<h1>Hello! I made this!</h1><p>This is my very first website.</p>' },
      { type: 'warning', value: 'Every opening tag like <html> needs a closing tag like </html>. The closing tag has a / in it. Do not forget this!' },
    ],
    task: {
      title: 'Create Your First Website',
      steps: [
        'In VS Code, right-click in the Explorer panel and click "New File"',
        'Name it "index.html" (this name is special — browsers look for it first)',
        'Type the HTML boilerplate code from above (do not copy-paste, typing helps you learn!)',
        'Press Ctrl+S to save',
        'Find the file in your my-website folder and double-click it',
        'It opens in Chrome! YOU MADE A WEBSITE! ',
      ],
    },
    challenge: {
      title: 'Make It Yours',
      description: 'Change the <h1> text to your name. Change the <p> text to tell people something about yourself. Save and refresh the browser to see your changes!',
    },
  },
  {
    id: 6, sectionId: 2, type: 'lesson', xp: 50,
    title: 'How Browsers Read HTML',
    subtitle: 'Top to bottom, tag by tag',
    content: [
      { type: 'hook', value: 'Have you ever wondered how the browser turns your code into a beautiful page? It reads your HTML like you read a book — from top to bottom, one line at a time.' },
      { type: 'text', value: 'When you open an HTML file, the browser does this:' },
      { type: 'list', ordered: true, items: [
        'Reads the <!DOCTYPE html> and says "okay, this is HTML"',
        'Goes into <html> and looks for <head> and <body>',
        'Reads <head> for invisible info (title, links to CSS files)',
        'Reads <body> and displays everything inside it, top to bottom',
      ] },
      { type: 'heading', value: 'What Happens If You Forget to Close a Tag?' },
      { type: 'text', value: 'The browser is pretty smart. If you forget to close a tag, it will try to fix it for you. But sometimes it guesses wrong and things look broken.' },
      { type: 'code', lang: 'html', title: 'Missing closing tag — Can you spot the error?', code: '<h1>My Heading\n<p>This paragraph might look weird because h1 was never closed!</p>' },
      { type: 'preview', title: 'What the browser shows', html: '<h1>My Heading<p>This paragraph might look weird because h1 was never closed!</p></h1>' },
      { type: 'tip', value: 'Always close your tags! Get into the habit now. When you type <h1>, immediately type </h1> and then go back and fill in the content between them.' },
    ],
    task: {
      title: 'Experiment Time',
      steps: [
        'Open your index.html file in VS Code',
        'Try removing the </h1> closing tag',
        'Save and refresh the browser — see what happens!',
        'Now put the </h1> back and save again',
        'See how closing tags matter?',
      ],
    },
    challenge: {
      title: 'Break and Fix',
      description: 'Intentionally break your HTML by removing different closing tags one at a time. See what each one does. Then fix them all back. This is how real developers learn!',
    },
  },
  {
    id: 7, sectionId: 3, type: 'lesson', xp: 50,
    title: 'Document Structure Deep Dive',
    subtitle: 'Understanding every part of the HTML skeleton',
    content: [
      { type: 'hook', value: 'You already wrote your first HTML file. Now let us truly understand every part of it. After this, you will know exactly what each line does and why it is there.' },
      { type: 'code', lang: 'html', title: 'The Full HTML Structure', code: '<!DOCTYPE html>\n<html lang="en">\n<head>\n  <meta charset="UTF-8">\n  <title>My Page</title>\n</head>\n<body>\n  <h1>Hello World</h1>\n</body>\n</html>' },
      { type: 'heading', value: 'What Lives in <head>?' },
      { type: 'text', value: 'The <head> section contains information ABOUT your page, but nothing that shows on screen:' },
      { type: 'list', ordered: false, items: [
        '<meta charset="UTF-8"> — This lets your page show special characters like Hindi letters (हिंदी), emojis, and symbols',
        '<title> — The text in the browser tab',
        'Later: links to CSS files, fonts, and other resources',
      ] },
      { type: 'heading', value: 'What Lives in <body>?' },
      { type: 'text', value: 'The <body> contains everything visible on your page — headings, text, images, links, everything your visitors will see.' },
      { type: 'tip', value: 'The lang="en" in the <html> tag tells browsers and screen readers that your page is in English. For Hindi, you would use lang="hi".' },
    ],
    task: {
      title: 'Update Your index.html',
      steps: [
        'Open your index.html in VS Code',
        'Add lang="en" to your <html> tag',
        'Add <meta charset="UTF-8"> inside your <head> tag',
        'Save and refresh — the page looks the same, but now it supports Hindi characters!',
        'Try adding some Hindi text in a <p> tag to test it',
      ],
    },
    challenge: {
      title: 'Hindi Test',
      description: 'Add a paragraph in Hindi to your page! Write something like <p>मेरा नाम ___ है</p> (fill in your name). See it display correctly because of UTF-8!',
    },
  },
  {
    id: 8, sectionId: 3, type: 'lesson', xp: 50,
    title: 'Headings: h1 to h6',
    subtitle: 'The size hierarchy of text',
    content: [
      { type: 'hook', value: 'Headings are like the chapter titles of a book. They tell people (and Google!) what each section of your page is about.' },
      { type: 'text', value: 'HTML has 6 levels of headings, from <h1> (biggest and most important) to <h6> (smallest). Think of it like a newspaper: the main headline is huge, sub-headlines are smaller.' },
      { type: 'code', lang: 'html', title: 'All 6 Heading Levels', code: '<h1>h1 — The Main Title</h1>\n<h2>h2 — Section Title</h2>\n<h3>h3 — Sub-section</h3>\n<h4>h4 — Detail</h4>\n<h5>h5 — Small Detail</h5>\n<h6>h6 — Smallest Heading</h6>' },
      { type: 'preview', title: 'How They Look', html: '<h1 style="margin:5px 0">h1 - The Main Title</h1><h2 style="margin:5px 0">h2 - Section Title</h2><h3 style="margin:5px 0">h3 - Sub-section</h3><h4 style="margin:5px 0">h4 - Detail</h4><h5 style="margin:5px 0">h5 - Small Detail</h5><h6 style="margin:5px 0">h6 - Smallest Heading</h6>' },
      { type: 'warning', value: 'Important rule: Use only ONE <h1> per page. It should be the main title. Use <h2> for sections, <h3> for sub-sections, and so on.' },
      { type: 'tip', value: 'Google uses headings to understand what your page is about. Good headings = better search results!' },
    ],
    task: {
      title: 'Add Headings to Your Page',
      steps: [
        'Open your index.html in VS Code',
        'Keep your <h1> with your name',
        'Add an <h2> that says "About Me"',
        'Add another <h2> that says "My Hobbies"',
        'Add <h3> headings under hobbies for each hobby',
        'Save and refresh to see the hierarchy!',
      ],
    },
    challenge: {
      title: 'Structure Your Page',
      description: 'Create a page with at least 3 sections using <h2> and sub-sections using <h3>. Topics: About Me, My Family, My School. Add some placeholder text under each heading.',
    },
  },
  {
    id: 9, sectionId: 3, type: 'lesson', xp: 50,
    title: 'Paragraphs',
    subtitle: 'The building blocks of text',
    content: [
      { type: 'hook', value: 'Most of the text on the internet lives inside paragraph tags. Learning to use them properly is like learning to write good sentences.' },
      { type: 'text', value: 'The <p> tag creates a paragraph. The browser automatically adds space above and below each paragraph.' },
      { type: 'heading', value: 'Why Can Not You Just Press Enter?' },
      { type: 'text', value: 'Here is something surprising: pressing Enter in your HTML code does NOT create a new line on the page. Watch:' },
      { type: 'code', lang: 'html', title: 'This WON\'T create separate lines', code: 'Hello\nWorld\nThis is all\non one line in the browser!' },
      { type: 'preview', title: 'What the browser shows', html: 'Hello World This is all on one line in the browser!' },
      { type: 'text', value: 'To get separate blocks of text, you NEED the <p> tag:' },
      { type: 'code', lang: 'html', title: 'This WILL create separate paragraphs', code: '<p>Hello</p>\n<p>World</p>\n<p>Now these are separate paragraphs!</p>' },
      { type: 'preview', title: 'Proper paragraphs', html: '<p>Hello</p><p>World</p><p>Now these are separate paragraphs!</p>' },
    ],
    task: {
      title: 'Add Paragraphs',
      steps: [
        'Open your index.html',
        'Under each heading, add a <p> tag with real text about yourself',
        'Write at least 2-3 sentences in each paragraph',
        'Save and see how neat it looks with proper paragraphs!',
      ],
    },
    challenge: {
      title: 'Tell Your Story',
      description: 'Write a short "About Me" with at least 3 paragraphs. First paragraph: who you are. Second: what you love doing. Third: what you want to be when you grow up.',
    },
  },
  {
    id: 10, sectionId: 3, type: 'lesson', xp: 50,
    title: 'Text Formatting',
    subtitle: 'Making words stand out',
    content: [
      { type: 'hook', value: 'Sometimes you need to make a word BOLD or italic or highlighted. HTML has special tags for each of these. Let us learn them all!' },
      { type: 'code', lang: 'html', title: 'Text Formatting Tags', code: '<p><strong>Bold text</strong> is for important things.</p>\n<p><em>Italic text</em> is for emphasis.</p>\n<p><u>Underlined text</u> draws attention.</p>\n<p><s>Strikethrough</s> means something is removed.</p>\n<p><mark>Highlighted text</mark> is super visible.</p>\n<p><small>Small text</small> for less important info.</p>' },
      { type: 'preview', title: 'All Formatting Styles', html: '<p><strong>Bold text</strong> is for important things.</p><p><em>Italic text</em> is for emphasis.</p><p><u>Underlined text</u> draws attention.</p><p><s>Strikethrough</s> means something is removed.</p><p><mark>Highlighted text</mark> is super visible.</p><p><small>Small text</small> for less important info.</p>' },
      { type: 'text', value: 'You can also combine them! Put one tag inside another:' },
      { type: 'code', lang: 'html', title: 'Combining Tags', code: '<p><strong><em>Bold AND italic!</em></strong></p>' },
      { type: 'preview', title: 'Combined', html: '<p><strong><em>Bold AND italic!</em></strong></p>' },
      { type: 'tip', value: 'Use <strong> for important words, <em> for words you want to stress. Screen readers (used by blind people) read these differently!' },
    ],
    task: {
      title: 'Format Your Text',
      steps: [
        'Open your index.html',
        'Find your "About Me" paragraphs',
        'Make your name <strong>bold</strong> wherever it appears',
        'Add <em>italic</em> to something you feel strongly about',
        'Try using <mark> to highlight your favourite thing',
        'Save and see the difference!',
      ],
    },
    challenge: {
      title: 'Formatted Bio',
      description: 'Rewrite your About Me section using ALL the formatting tags: strong, em, u, mark, and small. Make your name bold, your hobbies italic, your dream job highlighted!',
    },
  },
  {
    id: 11, sectionId: 3, type: 'lesson', xp: 50,
    title: 'Line Breaks & Horizontal Rules',
    subtitle: 'Controlling spacing in your page',
    content: [
      { type: 'hook', value: 'Remember how pressing Enter does nothing in HTML? Here is how you actually control line breaks and add dividers to your page.' },
      { type: 'heading', value: 'The <br> Tag — Line Break' },
      { type: 'text', value: 'The <br> tag creates a line break (goes to the next line). It is a self-closing tag — it does not need a closing tag because there is nothing to put between an opening and closing tag.' },
      { type: 'code', lang: 'html', title: 'Using <br>', code: '<p>Line one<br>Line two<br>Line three</p>' },
      { type: 'preview', title: 'Line Breaks', html: '<p>Line one<br>Line two<br>Line three</p>' },
      { type: 'heading', value: 'The <hr> Tag — Horizontal Rule' },
      { type: 'text', value: 'The <hr> tag creates a horizontal line across the page. Great for separating sections. Also self-closing.' },
      { type: 'code', lang: 'html', title: 'Using <hr>', code: '<h2>Section 1</h2>\n<p>Some text here.</p>\n<hr>\n<h2>Section 2</h2>\n<p>Different section here.</p>' },
      { type: 'preview', title: 'Horizontal Rule', html: '<h2>Section 1</h2><p>Some text here.</p><hr><h2>Section 2</h2><p>Different section here.</p>' },
      { type: 'warning', value: 'Do not use <br> to create space between paragraphs. Use separate <p> tags instead. <br> is for when you need a line break WITHIN a block of text, like in a poem or an address.' },
    ],
    task: {
      title: 'Add Breaks and Rules',
      steps: [
        'Open your index.html',
        'Add an <hr> between your main sections',
        'Write your home address using <br> for each line',
        'Save and see the clean separation!',
      ],
    },
    challenge: {
      title: 'Write a Poem',
      description: 'Write a short poem (or your favourite song lyrics) using <br> for line breaks within each stanza and <hr> between stanzas. Make the title an <h2>.',
    },
  },
  {
    id: 12, sectionId: 3, type: 'lesson', xp: 50,
    title: 'Comments & Special Characters',
    subtitle: 'Secret notes and magic symbols',
    content: [
      { type: 'hook', value: 'Did you know you can write secret notes in your code that nobody visiting your website will ever see? These are called comments, and every coder uses them.' },
      { type: 'heading', value: 'HTML Comments' },
      { type: 'text', value: 'Comments are invisible on the page. They are notes for yourself (or other coders) to explain what the code does.' },
      { type: 'code', lang: 'html', title: 'Comments', code: '<!-- This is a comment. The browser ignores it! -->\n<h1>This shows on the page</h1>\n<!-- TODO: Add more content later -->' },
      { type: 'heading', value: 'Special Characters' },
      { type: 'text', value: 'Some characters have special meaning in HTML (like < and >). To display them as text, you use special codes:' },
      { type: 'code', lang: 'html', title: 'Special Characters', code: '<p>&amp; = ampersand (&)</p>\n<p>&lt; = less than (<)</p>\n<p>&gt; = greater than (>)</p>\n<p>&copy; = copyright symbol</p>\n<p>&hearts; = heart</p>\n<p>&nbsp; = non-breaking space</p>' },
      { type: 'preview', title: 'Special Characters', html: '<p>&amp; = ampersand (&)</p><p>&lt; = less than (<)</p><p>&gt; = greater than (>)</p><p>&copy; = copyright symbol</p><p>&hearts; = heart</p><p>&nbsp;&nbsp;&nbsp; = three non-breaking spaces</p>' },
      { type: 'tip', value: 'Comments are great for leaving notes to yourself. Professional coders comment their code all the time!' },
    ],
    task: {
      title: 'Add Comments and Symbols',
      steps: [
        'Open your index.html',
        'Add a comment at the top: <!-- My personal website by [your name] -->',
        'Add a comment before each section explaining what it is',
        'At the bottom, add a footer with &copy; and your name',
        'Try using &hearts; somewhere fun!',
      ],
    },
    challenge: {
      title: 'Footer Challenge',
      description: 'Create a footer at the bottom of your page that says: (c) 2025 [Your Name] | Made with love. Use &copy; for the copyright symbol and &hearts; for the heart!',
    },
  },
];
